<script setup>
import { computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency.js'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'

const props = defineProps({
  rental: { type: Object, required: true },
  conflict: { type: Object, default: null }, // { id, start, customer } when re-rendered after a 422
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const { formatMoney } = useCurrency()

const fromLocalInput = (s) => s ? new Date(s).toISOString() : ''

// Default new return = current expected_end_at + 1 day
const defaultNewEnd = () => {
  const d = new Date(props.rental.expected_end_at)
  d.setDate(d.getDate() + 1)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const form = useForm({
  extension: {
    new_end_at:           defaultNewEnd(),
    daily_rate_cents:     String(props.rental.daily_rate_cents),
    collect_payment:      '1',
    payment_method:       'cash',
    payment_amount_cents: '',
    notes:                '',
    conflict_override:    '0',
  },
})

const daysAdded = computed(() => {
  const prev = new Date(props.rental.expected_end_at).getTime()
  const next = new Date(form.extension.new_end_at).getTime()
  if (!next || next <= prev) return 0
  return Math.max(1, Math.ceil((next - prev) / 86_400_000))
})

const totalAdded = computed(() => daysAdded.value * Number(form.extension.daily_rate_cents || 0))

watch(totalAdded, (v) => {
  if (form.extension.collect_payment === '1' && !form.extension.payment_amount_cents) {
    form.extension.payment_amount_cents = String(v)
  }
}, { immediate: true })

const collectOptions = computed(() => [
  { value: '1', label: t('common.yes') },
  { value: '0', label: t('common.no') },
])

const methodOptions = computed(() => [
  { value: 'cash',          label: t('rental.show.paymentModal.methods.cash') },
  { value: 'card',          label: t('rental.show.paymentModal.methods.card') },
  { value: 'bank_transfer', label: t('rental.show.paymentModal.methods.bank_transfer') },
  { value: 'online',        label: t('rental.show.paymentModal.methods.online') },
])

const blockedByConflict = computed(() => props.conflict && form.extension.conflict_override !== '1')

const submit = () => {
  if (daysAdded.value < 1) return
  if (blockedByConflict.value) return
  // Convert local datetime back to ISO before sending
  const payload = {
    ...form.extension,
    new_end_at: fromLocalInput(form.extension.new_end_at),
  }
  form.transform(() => ({ extension: payload })).post(`/rentals/${props.rental.id}/extensions`, {
    preserveScroll: true,
    onSuccess: () => emit('close'),
  })
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
    @click.self="$emit('close')"
  >
    <div class="w-full h-full md:h-auto md:max-w-lg md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
      <div class="px-5 py-4 md:px-6 md:py-5 border-b border-[color:var(--line)] flex items-center justify-between flex-shrink-0">
        <h2 class="text-base font-semibold text-[color:var(--ink)]">
          {{ t('rental.show.extension.modal.title') }}
        </h2>
        <button
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--ink)] text-xl leading-none"
          @click="$emit('close')"
        >
          ×
        </button>
      </div>

      <form
        id="extension-form"
        class="px-5 py-4 md:px-6 md:py-5 flex-1 overflow-y-auto space-y-4"
        @submit.prevent="submit"
      >
        <div
          v-if="conflict"
          class="rounded-[var(--radius-md)] border border-[color:var(--warn)] bg-[color:var(--warnSoft,rgba(251,191,36,0.12))] p-3 text-sm space-y-2"
        >
          <div class="text-[color:var(--ink)]">
            {{ t('rental.show.extension.modal.conflict_warning', { id: conflict.id, start: conflict.start, customer: conflict.customer }) }}
          </div>
          <label class="flex items-center gap-2 text-[color:var(--ink)]">
            <input
              v-model="form.extension.conflict_override"
              type="checkbox"
              true-value="1"
              false-value="0"
            >
            {{ t('rental.show.extension.modal.conflict_override') }}
          </label>
        </div>

        <div>
          <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.new_end_at') }}</label>
          <UiInput
            v-model="form.extension.new_end_at"
            type="datetime-local"
          />
        </div>

        <div>
          <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.daily_rate') }}</label>
          <UiInput
            v-model="form.extension.daily_rate_cents"
            type="number"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div class="text-[color:var(--muted)]">
              {{ t('rental.show.extension.modal.days_added') }}
            </div>
            <div class="fw-value tabular-nums text-[color:var(--ink)]">
              {{ daysAdded }}
            </div>
          </div>
          <div>
            <div class="text-[color:var(--muted)]">
              {{ t('rental.show.extension.modal.total_added') }}
            </div>
            <div class="fw-value tabular-nums text-[color:var(--ink)]">
              {{ formatMoney(totalAdded) }}
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.collect_now') }}</label>
          <UiSegmented
            v-model="form.extension.collect_payment"
            :options="collectOptions"
          />
        </div>

        <template v-if="form.extension.collect_payment === '1'">
          <div>
            <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.payment_method') }}</label>
            <UiSelect
              v-model="form.extension.payment_method"
              :options="methodOptions"
            />
          </div>
          <div>
            <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.amount') }}</label>
            <UiInput
              v-model="form.extension.payment_amount_cents"
              type="number"
            />
          </div>
        </template>

        <div>
          <label class="block text-sm text-[color:var(--inkSoft)] mb-1">{{ t('rental.show.extension.modal.notes') }}</label>
          <UiTextarea
            v-model="form.extension.notes"
            :rows="2"
          />
        </div>
      </form>

      <div class="px-5 md:px-6 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)] md:pb-5 border-t border-[color:var(--line)] flex gap-3 justify-end flex-shrink-0">
        <UiButton
          variant="ghost"
          @click="$emit('close')"
        >
          {{ t('rental.show.extension.modal.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          type="submit"
          form="extension-form"
          :loading="form.processing"
          :disabled="daysAdded < 1 || blockedByConflict"
        >
          {{ t('rental.show.extension.modal.submit') }}
        </UiButton>
      </div>
    </div>
  </div>
</template>
